import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';

import { getChannelEvents, getChannelBrowsers, getChannelDeviceTypes, getChannelAudience, getChannelRegistrations, getChannelDurations, getChannelViews, getChannelWatchTime, getChannelRetentionAverage, getChannelRetentionBuckets, getChannelRegions } from '../../../../../store/actions/admin/analytics';
import { EventsState } from '../../../../../store/types';
import ListHeader from '../../components/Scaffolding/ListHeader';
import { AnalyticsEvent } from '../../../../../types/working-model';
import Scorecard from '../../components/Scorecard/Scorecard';
// import Pie from '../../components/PieChart/Pie';
import EventListElement from '../../components/EventListElement';
// import SearchBar from '../../components/SearchBar';
// import { sessionData } from '../../data/sessionData/fake-session-data';
// import BarChartAudienceLocation from '../../components/BarChart/BarChartAudienceLocation';
import BarChartAudienceRetention from '../../components/BarChart/BarChartAudienceRetention';
// import AudienceMap from '../../components/Map/AudienceMap';
// import SessionTimeline from '../../components/SessionTimeline/SessionTimeline';
import ComponentWithHeader from '../../components/Scaffolding/ComponentWithHeader';
import AnimatedPieSVG from '../../components/PieChart/PieChartAnimated';
import usePrevious from '../../../../../utils/use-previous';

export const tooltipText = {
	sessions: "The number of individual session pages that have been created.",
	events: "The number of events that have been planned or have occurred.",
	totalViews: "The total number of page views.",
	avgDuration: "The average connection time (minutes) each page was viewed.",
	avgAudience: "The average number of unique visitors to each page.",
	totalRegistrations: "The total number of registrations recorded.",
	totalWatchtime: "The total active time (minutes) an individual is on the page.",
	retentionPercent: "The percentage of streamed content viewed. For example, a video playing for 100 minutes that is viewed on average for 60 minutes will have a retention rate of 60%.",
	deviceType: "The breakdown of what types of devices have viewed the page.",
	region: "The breakdown of what region the viewers reside in.",
	browser: "The breakdown of what browsers the viewers are using.",
	audienceRetention: "The breakdown of retention for streamable content.",
	timeline: "A timeline of the number of page views based on the time recorded.",
	retentionStats: "A breakdown of the retention statistics.",
	audienceLocation: "Hover and click on any section of the map to bring up viewer statistics for that region. Click and drag to move the map, or scroll within the map window to zoom.",
	emails: "The number of registered emails.",
	languages: "The number of translated languages.",
	customRegistration: "This metric is created automatically based on registration data.",
};

const sortOptions = [
	{
		label: 'Newest',
		value: 'newest',
	},
	{
		label: 'Oldest',
		value: 'oldest',
	},
	{
		label: 'A-Z',
		value: 'a-z',
	},
	{
		label: 'Z-A',
		value: 'z-a',
	},
];

function ClientLevelAnalytics(): JSX.Element {
	const {
		AuthReducer: { user, token },
		AnalyticsReducer: { channelEvents, channelDevices, channelBrowsers, channelAudience, channelRegistrations, channelWatchtime, channelDuration, channelViews, channelRetentionAverage, channelRetentionBuckets, channelRegions }
	} = useSelector((state: EventsState) => state);

	const dispatch = useDispatch();
	const lastChannel = usePrevious(user?.active_channel);

	useEffect(() => {
		if (lastChannel != user?.active_channel) {
			batch(() => {
				dispatch(getChannelEvents(user!.active_channel, token!));
				dispatch(getChannelDeviceTypes(user!.active_channel, token!));
				dispatch(getChannelBrowsers(user!.active_channel, token!));
				dispatch(getChannelAudience(user!.active_channel, token!));
				dispatch(getChannelRegistrations(user!.active_channel, token!));
				dispatch(getChannelDurations(user!.active_channel, token!));
				dispatch(getChannelViews(user!.active_channel, token!));
				dispatch(getChannelWatchTime(user!.active_channel, token!));
				dispatch(getChannelRetentionAverage(user!.active_channel, token!));
				dispatch(getChannelRetentionBuckets(user!.active_channel, token!));
				dispatch(getChannelRegions(user!.active_channel, token!));
			});
		}
	}, [user, lastChannel, token, dispatch]);

	// const [sessionDatas, setSessionDatas] = useState(sessionData);
	// const [timelineData, setTimelineData] = useState(clientData.sessionTimeLine);
	const sessionsCount = channelEvents.data.reduce((sum, event) => sum + event.sessions.length, 0);

	const [filteredEvents, setFilteredEvents] = useState<AnalyticsEvent[]>([]);
	const [sortBy, setSortBy] = useState('');

	useEffect(() => {
		setFilteredEvents(channelEvents.data);
	}, [channelEvents]);

	const handleSearch = (search: string): void => {
		const filteredEvents: AnalyticsEvent[] = channelEvents.data.filter((event: AnalyticsEvent) => event.name.toLowerCase().includes(search.toLowerCase().trim()));
		setFilteredEvents(filteredEvents);
	};

	const handleClear = (): void => {
		setFilteredEvents(channelEvents.data);
	};

	const sortedEvents = useMemo((): AnalyticsEvent[] => {
		const sortedList = [...filteredEvents];
		switch (sortBy) {
			case 'oldest': {
				sortedList.sort((a, b) => {
					const aStart = a.start_time || 0;
					const bStart = b.start_time || 0;
					return aStart - bStart;
				});
				break;
			}
			case 'a-z': {
				sortedList.sort((a, b) => {
					const nameA = a.name.toLowerCase();
					const nameB = b.name.toLowerCase();
					if (nameA < nameB) return -1;
					if (nameA > nameB) return 1;
					return 0;
				});
				break;
			}
			case 'z-a': {
				sortedList.sort((a, b) => {
					const nameA = a.name.toLowerCase();
					const nameB = b.name.toLowerCase();
					if (nameA > nameB) return -1;
					if (nameA < nameB) return 1;
					return 0;
				});
				break;
			}
			case 'newest':
			default: {
				sortedList.sort((a, b) => {
					const aStart = a.start_time || 0;
					const bStart = b.start_time || 0;
					return bStart - aStart;
				});
			}
		}
		return sortedList;
	}, [sortBy, filteredEvents]);

	return (
		<div className="data-analytics">
			<div className="channel-metrics-grid">
				<Scorecard index={0} data={{ value: sessionsCount }} title="Sessions" showTL={false} showMM={false} loading={channelEvents.loading} error={channelEvents.error} tooltipText={tooltipText.sessions} />
				<Scorecard index={1} data={{ value: channelEvents.data.length }} title="Events" showTL={false} showMM={false} loading={channelEvents.loading} error={channelEvents.error} tooltipText={tooltipText.events} />
				<Scorecard index={2} data={channelViews.data} title="Total views" showTL={true} showMM={false} interval="event" loading={channelViews.loading} error={channelViews.error} tooltipText={tooltipText.totalViews} />
				<Scorecard index={3} data={channelDuration.data} title="Avg. Duration, min" showTL={true} showMM={false} interval="event" loading={channelDuration.loading} error={channelDuration.error} tooltipText={tooltipText.avgDuration} />
				<Scorecard index={4} data={channelAudience.data} title="Avg. Audience, users" showTL={true} showMM={true} interval="event" loading={channelDuration.loading} error={channelDuration.error} tooltipText={tooltipText.avgAudience} />
				<Scorecard index={5} data={channelRegistrations.data} title="Total Registrations" showTL={true} showMM={false} interval="event" loading={channelDuration.loading} error={channelDuration.error} tooltipText={tooltipText.totalRegistrations} />
				<Scorecard index={6} data={channelWatchtime.data} title="Total Watchtime, min" showTL={true} showMM={false} interval="event" loading={channelDuration.loading} error={channelDuration.error} tooltipText={tooltipText.totalWatchtime} />
				<Scorecard index={7} data={channelRetentionAverage.data} title="Retention, %" showTL={true} showMM={false} interval="event" loading={channelDuration.loading} error={channelDuration.error} tooltipText={tooltipText.retentionPercent} />
				{/* {donuts.map((donut:any,index:number)=>{})} */}

				<ComponentWithHeader X={"X2"} Y={"Y2"} id={["donut-chart", 0]} header={"Device Type"} tooltipText={tooltipText.deviceType}>
					<AnimatedPieSVG data={{ values: channelDevices.data }} index={0} title={"Device Type"} loading={channelDevices.loading} error={channelDevices.error} />
				</ComponentWithHeader>

				<ComponentWithHeader X={"X2"} Y={"Y2"} id={["donut-chart", 1]} header={"Region"} tooltipText={tooltipText.region}>
					<AnimatedPieSVG data={{ values: channelRegions.data }} index={1} title={"Region"} loading={channelRegions.loading} error={channelRegions.error} />
				</ComponentWithHeader>

				<ComponentWithHeader X={"X2"} Y={"Y2"} id={["donut-chart", 2]} header={"Browser"} tooltipText={tooltipText.browser}>
					<AnimatedPieSVG data={{ values: channelBrowsers.data }} index={2} title={"Browser"} loading={channelBrowsers.loading} error={channelBrowsers.error} />
				</ComponentWithHeader>

				{/*
				<ComponentWithHeader X={"X2"} Y={"Y2"} id={["donut-chart", 3]} header={"Interaction Score"}>
					<AnimatedPieSVG data={donuts.interactionDonut} index={3} title={"Interaction Score"} />
				</ComponentWithHeader>
				*/}

				<ComponentWithHeader X={"X8"} Y={"Y3"} id={["retention-chart", 0]} header={"Audience Retention"} tooltipText={tooltipText.audienceRetention}>
					<BarChartAudienceRetention width={1050} height={200} dataIn={channelRetentionBuckets.data} loading={channelRetentionBuckets.loading} error={channelRetentionBuckets.error} />
				</ComponentWithHeader>
				{/*
				<ComponentWithHeader X={"X2"} Y={"Y3"} id={["retention-stats", 0]} header={"Retention Stats"} tooltipText={tooltipText.retentionStats}>
					<BarChartAudienceRetention width={760} height={200} dataIn={retentionData}/>
				</ComponentWithHeader>
				 */}
				{/*
				<ComponentWithHeader X={"X8"} Y={"Y2"} id={["timeline-overview", 0]} header={"Timelive Overview"}>
					<BarChartAudienceRetention width={760} height={200} dataIn={retentionData} tooltipText={tooltipText.timeline}/>
				</ComponentWithHeader>
				*/}

			</div>

			{/* <ComponentWithHeader X={"X6"} Y={"Y3"} id={["session-timeline", 0]} header={"Session Timeline"}>
                <SessionTimeline width={760} height={220} dataIn={timelineData}/>
            </ComponentWithHeader>

            <ComponentWithHeader X={"X8"} Y={"Y4"} id={["world-map", 0]} header={"Audience Location"}>
                <AudienceMap width={608} height={280}/> 
                <BarChartAudienceLocation/>
            </ComponentWithHeader> */}

			<ListHeader
				activeTab={"Events"}
				activeTabCount={channelEvents.data.length}
				handleSearch={handleSearch}
				handleClear={handleClear}
				sortOptions={sortOptions}
				sortBy={sortBy}
				setSortBy={setSortBy}
			/>

			<div className="events-list">
				{sortedEvents.map((e) => <EventListElement key={e.event} eventData={e} />)}
			</div>

			{/* <AudienceMap /> */}
			{/* 1X1 Emails */}
			{/* 2X1 With Sparkchart */}
			{/* 2X2 Donut chart */}
			{/* 6X4 World chart with bar chart */}
			{/* 6X3 Session Timeline */}
		</div>
	);
}

export default ClientLevelAnalytics;
